// @ts-nocheck
import React, { useEffect, useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, TextInput, Modal,
} from 'react-native';
import { Colors, FontSize, FontWeight, Radii } from '../../theme/tokens';
import { useApp } from '../../store/AppStore';
import { useResponsive } from '../../theme/responsive';
import { ScreenShell } from '../../components/WebShell';
import { ComingSoon } from '../../components/ComingSoon';
import { Icon } from '../../components/Icon';
import PrimaryButton from '../../components/Button_Component/Primary_Button';
import { Chip } from '../../components/Chip';

const EVENT_TYPES = [
  'ticket.created', 'ticket.updated', 'comment.public_added', 'sla.breached',
];

const CONDITION_OPS = ['eq', 'ne', 'in', 'contains', 'gt', 'lt'];
const ACTION_TYPES = [
  'setPriority', 'setType', 'setStatus', 'setAssignedGroup',
  'addTags', 'removeTags', 'appendInternalComment', 'autoAssign',
];

// Inline dropdown that cycles through options on press (no extra deps,
// keeps the existing visual language of pill/option rows).
function InlineSelect({ value, options, onChange, style }: { value: string; options: string[]; onChange: (v: string) => void; style?: any }) {
  const [open, setOpen] = useState(false);
  return (
    <View style={[{ position: 'relative' }, style]}>
      <TouchableOpacity style={nmStyles.select} onPress={() => setOpen(o => !o)}>
        <Text style={nmStyles.selectText} numberOfLines={1}>{value}</Text>
        <Icon name="ChevronDown" size={12} color={Colors.ink3} />
      </TouchableOpacity>
      {open && (
        <View style={nmStyles.selectMenu}>
          <ScrollView style={{ maxHeight: 200 }}>
            {options.map(opt => (
              <TouchableOpacity
                key={opt}
                style={[nmStyles.option, value === opt && nmStyles.optionActive]}
                onPress={() => { onChange(opt); setOpen(false); }}
              >
                <Text style={[nmStyles.optionText, value === opt && nmStyles.optionTextActive]}>{opt}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

function RuleEditorModal({
  rule, onClose, onSave,
}: {
  rule?: any;
  onClose: () => void;
  onSave: (data: { name: string; trigger: string; conditions: any[]; actions: any[] }) => void;
}) {
  const { isMobile } = useResponsive();
  const isEdit = !!rule;
  const [name, setName] = useState(rule?.name || '');
  const [eventType, setEventType] = useState(rule?.trigger || 'ticket.created');
  const [conditions, setConditions] = useState<any[]>(
    Array.isArray(rule?.conditions)
      ? rule.conditions.map((c: any) => ({ field: c.field || '', op: c.op || 'eq', value: c.value ?? '' }))
      : []
  );
  const [actions, setActions] = useState<any[]>(
    Array.isArray(rule?.actions)
      ? rule.actions.map((a: any) => ({ type: a.type || ACTION_TYPES[0], param: a.param ?? '' }))
      : []
  );

  const addCondition = () => setConditions(c => [...c, { field: '', op: 'eq', value: '' }]);
  const updateCondition = (i: number, patch: any) => setConditions(c => c.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  const removeCondition = (i: number) => setConditions(c => c.filter((_, idx) => idx !== i));

  const addAction = () => setActions(a => [...a, { type: ACTION_TYPES[0], param: '' }]);
  const updateAction = (i: number, patch: any) => setActions(a => a.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  const removeAction = (i: number) => setActions(a => a.filter((_, idx) => idx !== i));

  const handleSave = () => {
    const cleanConditions = conditions
      .filter(c => c.field.trim())
      .map(c => ({ field: c.field.trim(), op: c.op, value: c.value }));
    const cleanActions = actions
      .filter(a => a.type)
      .map(a => ({ type: a.type, param: a.param }));
    onSave({ name: name.trim(), trigger: eventType, conditions: cleanConditions, actions: cleanActions });
  };

  return (
    <Modal transparent animationType="fade">
      <TouchableOpacity style={nmStyles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity style={nmStyles.modal} activeOpacity={1} onPress={() => {}}>
          <Text style={nmStyles.title}>{isEdit ? 'Edit automation rule' : 'New automation rule'}</Text>
          <ScrollView style={{ maxHeight: 480 }}>
            <Text style={nmStyles.label}>Rule name</Text>
            <TextInput style={nmStyles.input} value={name} onChangeText={setName} placeholder="e.g. Auto-assign billing" autoFocus />

            <Text style={nmStyles.label}>Trigger event</Text>
            <InlineSelect value={eventType} options={EVENT_TYPES} onChange={setEventType} />

            <View style={nmStyles.builderHead}>
              <Text style={nmStyles.label}>Conditions</Text>
              <TouchableOpacity onPress={addCondition} style={nmStyles.addRowBtn}>
                <Icon name="Plus" size={12} color={Colors.accent} />
                <Text style={nmStyles.addRowText}>Add condition</Text>
              </TouchableOpacity>
            </View>
            {conditions.length === 0 && <Text style={nmStyles.emptyHint}>Runs on every event of this type.</Text>}
            {conditions.map((c, i) => (
              <View key={i} style={[nmStyles.row, isMobile && { flexDirection: 'column', alignItems: 'stretch' }]}>
                <TextInput style={[nmStyles.input, nmStyles.rowField, isMobile && { flex: undefined }]} value={c.field} onChangeText={t => updateCondition(i, { field: t })} placeholder="field" />
                <InlineSelect value={c.op} options={CONDITION_OPS} onChange={v => updateCondition(i, { op: v })} style={[nmStyles.rowOp, isMobile && { width: '100%' as any }]} />
                <TextInput style={[nmStyles.input, nmStyles.rowField, isMobile && { flex: undefined }]} value={String(c.value)} onChangeText={t => updateCondition(i, { value: t })} placeholder="value" />
                <TouchableOpacity onPress={() => removeCondition(i)} style={nmStyles.removeRowBtn}>
                  <Icon name="X" size={13} color={Colors.ink3} />
                </TouchableOpacity>
              </View>
            ))}

            <View style={nmStyles.builderHead}>
              <Text style={nmStyles.label}>Actions</Text>
              <TouchableOpacity onPress={addAction} style={nmStyles.addRowBtn}>
                <Icon name="Plus" size={12} color={Colors.accent} />
                <Text style={nmStyles.addRowText}>Add action</Text>
              </TouchableOpacity>
            </View>
            {actions.length === 0 && <Text style={nmStyles.emptyHint}>No actions yet — add at least one.</Text>}
            {actions.map((a, i) => (
              <View key={i} style={[nmStyles.row, isMobile && { flexDirection: 'column', alignItems: 'stretch' }]}>
                <InlineSelect value={a.type} options={ACTION_TYPES} onChange={v => updateAction(i, { type: v })} style={[nmStyles.rowAction, isMobile && { flex: undefined }]} />
                <TextInput style={[nmStyles.input, nmStyles.rowField, isMobile && { flex: undefined }]} value={String(a.param)} onChangeText={t => updateAction(i, { param: t })} placeholder="value" />
                <TouchableOpacity onPress={() => removeAction(i)} style={nmStyles.removeRowBtn}>
                  <Icon name="X" size={13} color={Colors.ink3} />
                </TouchableOpacity>
              </View>
            ))}
          </ScrollView>
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 16 }}>
            <PrimaryButton title="Cancel" size="md" variant="default" onPress={onClose} style={{ flex: 1 }} />
            <PrimaryButton title={isEdit ? 'Save changes' : 'Create rule'} size="md" variant="primary" disabled={!name.trim()} onPress={handleSave} style={{ flex: 1 }} />
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}

const nmStyles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center' },
  modal: { backgroundColor: '#fff', borderRadius: 16, padding: 24, width: 480, maxWidth: '90%' as any },
  title: { fontSize: 17, fontWeight: '700', color: Colors.ink, marginBottom: 16 },
  label: { fontSize: 12, fontWeight: '600', color: Colors.ink3, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, marginTop: 12 },
  input: { borderWidth: 1, borderColor: Colors.line, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8, fontSize: 14, color: Colors.ink },
  option: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6, marginBottom: 2 },
  optionActive: { backgroundColor: Colors.accentSoft },
  optionText: { fontSize: 13, color: Colors.ink2 },
  optionTextActive: { color: Colors.accent, fontWeight: '600' },
  select: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1, borderColor: Colors.line, borderRadius: 8,
    paddingHorizontal: 10, paddingVertical: 8,
  },
  selectText: { fontSize: 13, color: Colors.ink, flex: 1 },
  selectMenu: {
    position: 'absolute', top: '100%' as any, left: 0, right: 0, zIndex: 10,
    backgroundColor: '#fff', borderWidth: 1, borderColor: Colors.line, borderRadius: 8,
    marginTop: 2, padding: 4,
    shadowColor: '#101828', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.12, shadowRadius: 8,
  },
  builderHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  addRowBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 12 },
  addRowText: { fontSize: 12, fontWeight: '600', color: Colors.accent },
  emptyHint: { fontSize: 12, color: Colors.ink3, marginTop: 4 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 },
  rowField: { flex: 1, paddingVertical: 6 },
  rowOp: { width: 96 },
  rowAction: { flex: 1 },
  removeRowBtn: { width: 26, height: 26, justifyContent: 'center', alignItems: 'center', borderRadius: 6 },
});

// ─── Toggle ───────────────────────────────────────────────────────────────────

function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <TouchableOpacity
      onPress={onToggle}
      style={[styles.toggle, { backgroundColor: on ? Colors.accent : Colors.paper3, borderColor: on ? Colors.accent : Colors.lineStrong }]}
    >
      <View style={[styles.toggleThumb, { transform: [{ translateX: on ? 14 : 1 }] }]} />
    </TouchableOpacity>
  );
}

// ─── AutomationsScreen ────────────────────────────────────────────────────────

export function AutomationsScreen() {
  const { automations, loadAutomations, toggleAutomation, deleteAutomation, createAutomation, updateAutomation } = useApp();
  const { isMobile, pagePadX } = useResponsive();
  const [showNewRule, setShowNewRule] = useState(false);
  const [editingRule, setEditingRule] = useState<any | null>(null);
  const formatConditions = (conditions: any): string => {
    if (!conditions) return '';
    if (Array.isArray(conditions)) {
      return conditions
        .map((c: any) => (typeof c === 'string' ? c : `${c.field} ${c.op} ${c.value}`))
        .join(', ');
    }
    if (typeof conditions === 'object') {
      return Object.entries(conditions).map(([k, v]) => `${k} = ${v}`).join(', ');
    }
    return String(conditions);
  };

  useEffect(() => {
    loadAutomations();
  }, []);

  const handleCreateRule = async (data: { name: string; trigger: string; conditions: any[]; actions: any[] }) => {
    setShowNewRule(false);
    await createAutomation({ name: data.name, trigger: data.trigger, enabled: true, conditions: data.conditions, actions: data.actions });
  };

  const handleUpdateRule = async (data: { name: string; trigger: string; conditions: any[]; actions: any[] }) => {
    if (!editingRule) return;
    const id = editingRule.id;
    setEditingRule(null);
    await updateAutomation(id, { name: data.name, trigger: data.trigger, enabled: editingRule.enabled ?? true, conditions: data.conditions, actions: data.actions });
  };

  // Format trigger string from eventType
  const formatTrigger = (eventType: string, conditions: any) => {
    if (!eventType) return 'Unknown trigger';
    const parts = [eventType];
    if (conditions && typeof conditions === 'object') {
      const entries = Object.entries(conditions);
      if (entries.length > 0) {
        parts.push(entries.map(([k, v]) => `${k} = ${v}`).join(', '));
      }
    }
    return parts.join(' · ');
  };

  // Format actions from API
  const formatActions = (actions: any): string[] => {
    if (!actions) return [];
    if (Array.isArray(actions)) {
      return actions.map((a: any) => {
        if (typeof a === 'string') return a;
        if (a.type) return a.param ? `${a.type}: ${a.param}` : a.type;
        return JSON.stringify(a);
      });
    }
    if (typeof actions === 'object') {
      return Object.entries(actions).map(([k, v]) => `${k}: ${v}`);
    }
    return [String(actions)];
  };

  const totalRuns = automations.reduce((s, r) => s + (r.runs || 0), 0);
  const activeCount = automations.filter(r => r.enabled).length;

  // ── Showing the premium "coming soon" card for now. Original Automations
  //    screen preserved below (unreachable) — remove this return to restore. ──
  return (
    <ComingSoon
      crumbs={[{ label: 'Settings' }, { label: 'Automations' }]}
      icon="bolt"
      title="Automations"
      subtitle="Let the busywork run itself — smart rules that triage, route and reply automatically. We're building it to feel like magic, and it's almost here."
    />
  );

  return (
    <ScreenShell crumbs={[
      { label: 'Settings' },
      { label: 'Automations' },
    ]}>
      <View style={styles.root}>
        <View style={[styles.pageHead, { paddingHorizontal: pagePadX }, isMobile && { flexWrap: 'wrap' }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pageTitle}>Automations</Text>
            <Text style={styles.pageSub}>When [trigger], do [actions]. Use for routing, SLAs, and bulk operations.</Text>
          </View>
          <View style={styles.headerBtns}>
            <PrimaryButton
              title="New rule"
              size="md"
              variant="primary"
              leftIcon={<Icon name="Plus" size={14} color={Colors.paper} />}
              onPress={() => setShowNewRule(true)}
            />
          </View>
        </View>

        <ScrollView style={styles.body} contentContainerStyle={[styles.bodyContent, { padding: isMobile ? 16 : 24 }]}>
          <Text style={styles.sectionTitle}>Rules</Text>
          <Text style={styles.sectionSub}>
            {activeCount} of {automations.length} active · {totalRuns} runs this month
          </Text>

          <View style={styles.rulesList}>
            {automations.length === 0 && (
              <Text style={{ color: Colors.ink3, fontSize: 13, textAlign: 'center', paddingVertical: 24 }}>
                No automation rules yet.
              </Text>
            )}
            {automations.map(r => {
              const actions = formatActions(r.actions);
              const trigger = typeof r.trigger === 'string' ? r.trigger : formatTrigger(r.trigger, r.conditions);
              const condText = formatConditions(r.conditions);
              return (
                <View key={r.id} style={styles.ruleCard}>
                  {/* Rule header */}
                  <View style={styles.ruleHead}>
                    <View style={[
                      styles.ruleBoltIcon,
                      { backgroundColor: r.enabled ? Colors.accentSoft : Colors.paper3 },
                    ]}>
                      <Icon name="Bolt" size={14} color={r.enabled ? Colors.accent : Colors.ink4} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.ruleName}>{r.name || 'Untitled rule'}</Text>
                      <Text style={styles.ruleRuns}>{r.runs || 0} runs in last 30 days</Text>
                    </View>
                    <Chip
                      label={r.enabled ? 'Enabled' : 'Paused'}
                      tone={r.enabled ? 'success' : 'neutral'}
                      dot
                      size="sm"
                    />
                    <Toggle on={r.enabled} onToggle={() => toggleAutomation(r.id)} />
                    <TouchableOpacity
                      style={styles.editBtn}
                      onPress={() => setEditingRule(r)}
                    >
                      <Icon name="Edit" size={13} color={Colors.ink3} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.editBtn}
                      onPress={() => deleteAutomation(r.id)}
                    >
                      <Icon name="X" size={13} color={Colors.ink3} />
                    </TouchableOpacity>
                  </View>

                  {/* When / Then */}
                  <View style={styles.ruleBody}>
                    <View style={styles.ruleSection}>
                      <Text style={styles.ruleSectionLabel}>WHEN</Text>
                      <Text style={styles.ruleTrigger}>{trigger}</Text>
                      {!!condText && (
                        <View style={styles.actionRow}>
                          <Icon name="ArrowRight" size={12} color={Colors.ink4} />
                          <Text style={styles.actionText}>{condText}</Text>
                        </View>
                      )}
                    </View>
                    <View style={[styles.ruleSection, { flex: 2, borderLeftWidth: 0 }]}>
                      <Text style={styles.ruleSectionLabel}>THEN</Text>
                      {actions.map((a, i) => (
                        <View key={i} style={styles.actionRow}>
                          <Icon name="ArrowRight" size={12} color={Colors.ink4} />
                          <Text style={styles.actionText}>{a}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                </View>
              );
            })}
          </View>

          {/* Ideas box */}
          <View style={styles.ideasBox}>
            <Icon name="Bolt" size={18} color={Colors.accent} />
            <View style={{ flex: 1 }}>
              <Text style={styles.ideasTitle}>Looking for ideas?</Text>
              <Text style={styles.ideasSub}>Common rules: round-robin assignment, auto-tag by keyword, escalate after N hours, send CSAT after resolve.</Text>
            </View>
          </View>
        </ScrollView>
      </View>
      {showNewRule && <RuleEditorModal onClose={() => setShowNewRule(false)} onSave={handleCreateRule} />}
      {editingRule && <RuleEditorModal rule={editingRule} onClose={() => setEditingRule(null)} onSave={handleUpdateRule} />}
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  pageHead: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 16,
    paddingHorizontal: 28, paddingTop: 20, paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    backgroundColor: Colors.card, flexShrink: 0,
  },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageSub: { fontSize: 13, color: Colors.ink3, marginTop: 4 },
  headerBtns: { flexDirection: 'row', gap: 8 },
  toggle: {
    width: 32, height: 18, borderRadius: 999, borderWidth: 1,
    justifyContent: 'center', marginLeft: 6,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  toggleThumb: {
    width: 14, height: 14, borderRadius: 99,
    backgroundColor: Colors.white,
    shadowColor: '#101828', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.18, shadowRadius: 2,
  },
  body: { flex: 1 },
  bodyContent: { maxWidth: 1080, alignSelf: 'center', width: '100%' as any, padding: 24 },
  sectionTitle: { fontSize: 14, fontWeight: FontWeight.semibold, color: Colors.ink },
  sectionSub: { fontSize: 12.5, color: Colors.ink3, marginTop: 2, marginBottom: 12 },
  rulesList: { gap: 12 },
  ruleCard: {
    backgroundColor: Colors.card,
    borderRadius: Radii.r3,
    borderWidth: 1, borderColor: Colors.line,
    overflow: 'hidden',
  },
  ruleHead: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 18, paddingVertical: 14,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
  },
  ruleBoltIcon: {
    width: 28, height: 28, borderRadius: Radii.r2,
    justifyContent: 'center', alignItems: 'center',
    flexShrink: 0, marginRight: 4,
  },
  ruleName: { fontSize: 14, fontWeight: FontWeight.semibold, color: Colors.ink },
  ruleRuns: { fontSize: 11.5, color: Colors.ink3, marginTop: 2 },
  editBtn: {
    width: 28, height: 28, justifyContent: 'center', alignItems: 'center',
    borderRadius: Radii.r1, marginLeft: 2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  ruleBody: { flexDirection: 'row' },
  ruleSection: {
    flex: 1, padding: 14,
    borderRightWidth: 1, borderRightColor: Colors.line,
  },
  ruleSectionLabel: {
    fontSize: 10.5, fontWeight: FontWeight.bold,
    color: Colors.ink4, textTransform: 'uppercase',
    letterSpacing: 0.6, marginBottom: 6,
  },
  ruleTrigger: { fontSize: 13, color: Colors.ink },
  actionRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 },
  actionText: { fontSize: 13, color: Colors.ink },
  ideasBox: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    marginTop: 24, padding: 14,
    backgroundColor: Colors.card,
    borderWidth: 1, borderStyle: 'dashed' as any, borderColor: Colors.lineStrong,
    borderRadius: Radii.r3,
  },
  ideasTitle: { fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink },
  ideasSub: { fontSize: 12, color: Colors.ink3, marginTop: 2 },
});
